import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatBRL, formatPerWeight, formatDate, WEIGHT_UNITS } from "@/lib/api";
import {
  ArrowLeft,
  ArrowsClockwise,
  ArrowUpRight,
  Storefront,
  TrendUp,
  TrendDown,
  Equals,
  Clock,
  Info,
  Anchor,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";
import UnitToggle from "@/components/UnitToggle";

export default function TrackedDetail() {
  const { id } = useParams();
  const { unit, settings } = useSettings();
  const [data, setData] = useState(null);
  const [links, setLinks] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [channelFilter, setChannelFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([api.get(`/tracked/${id}`), api.get(`/tracked/${id}/links`)]);
      setData(r1.data);
      setLinks(r2.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post(`/tracked/${id}/refresh`);
      setData(r.data);
      toast.success("Ofertas atualizadas");
    } catch {
      toast.error("Erro ao atualizar");
    } finally { setRefreshing(false); }
  };

  if (loading) return <div className="text-sm" style={{ color: "var(--text-secondary)" }}>Carregando…</div>;
  if (!data) return <div className="text-sm" style={{ color: "var(--text-secondary)" }}>Não encontrado</div>;

  const offersAll = data.offers || [];
  const channelsInData = Array.from(new Set(offersAll.map((o) => o.channel)));
  const filtered = channelFilter ? offersAll.filter((o) => o.channel === channelFilter) : offersAll;
  const offers = [...filtered].sort((a, b) => (a.normalized_price ?? Infinity) - (b.normalized_price ?? Infinity));

  // Recompute stats based on filter
  const filterPrices = filtered.filter((o) => o.normalized_price).map((o) => o.normalized_price);
  const stats = channelFilter && filterPrices.length
    ? {
        min: Math.min(...filterPrices),
        max: Math.max(...filterPrices),
        avg: filterPrices.reduce((a, b) => a + b, 0) / filterPrices.length,
        count: filterPrices.length,
      }
    : { min: data.min_norm, avg: data.avg_norm, max: data.max_norm, count: data.normalized_count };

  const unitLabel = WEIGHT_UNITS.find((u) => u.value === unit)?.label || "100g";
  const anchoredSet = new Set((settings.anchored_channels || []).map((c) => c.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="tracked-detail">
      <Link to="/rastrear" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider" style={{ color: "var(--gold)" }}>
        <ArrowLeft size={14} /> Voltar
      </Link>

      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="label-eyebrow mb-2">Detalhes do produto</div>
          <h1 className="text-3xl md:h-display" style={{ color: "var(--text-primary)" }}>
            {data.query}
          </h1>
          <div className="text-xs mt-2 flex items-center gap-1" style={{ color: "var(--text-muted)" }}>
            <Clock size={12} /> Atualizado em {formatDate(data.last_updated)}
          </div>
        </div>
        <button onClick={refresh} disabled={refreshing} className="btn-gold px-4 py-2.5 rounded-sm text-xs uppercase tracking-wider flex items-center gap-2" data-testid="refresh-detail">
          <ArrowsClockwise size={14} className={refreshing ? "spinner" : ""} /> {refreshing ? "Atualizando…" : "Atualizar ofertas"}
        </button>
      </header>

      <div className="surface p-3 flex items-center justify-between flex-wrap gap-3">
        <UnitToggle />
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <span className="label-eyebrow">Canal</span>
          <button
            onClick={() => setChannelFilter("")}
            className="px-2 py-1 text-xs"
            style={{
              background: !channelFilter ? "var(--gold)" : "transparent",
              color: !channelFilter ? "var(--bg-base)" : "var(--text-secondary)",
              border: "1px solid var(--border-default)",
            }}
            data-testid="filter-all"
          >Todos</button>
          {channelsInData.map((ch) => (
            <button
              key={ch}
              onClick={() => setChannelFilter(ch)}
              className="px-2 py-1 text-xs"
              style={{
                background: channelFilter === ch ? "var(--gold)" : "transparent",
                color: channelFilter === ch ? "#ffffff" : "var(--text-secondary)",
                border: "1px solid var(--border-default)",
              }}
              data-testid={`filter-${ch}`}
            >{ch}</button>
          ))}
        </div>
      </div>

      {data.normalized_count < data.offer_count && (
        <div className="flex items-start gap-2 text-xs px-3 py-2" style={{ color: "var(--warning)", background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.2)" }}>
          <Info size={14} weight="duotone" className="mt-0.5" />
          <span>
            {data.offer_count - data.normalized_count} oferta(s) sem peso identificável no título foram excluídas do cálculo normalizado.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Stat label="Ofertas" value={stats.count} icon={Storefront} />
        <Stat label={`Menor /${unitLabel}`} value={formatPerWeight(stats.min, unit)} icon={TrendDown} color="var(--success)" />
        <Stat label={`Médio /${unitLabel}`} value={formatPerWeight(stats.avg, unit)} icon={Equals} color="var(--gold)" />
        <Stat label={`Maior /${unitLabel}`} value={formatPerWeight(stats.max, unit)} icon={TrendUp} color="var(--danger)" />
      </div>

      <section className="surface" data-testid="external-links">
        <div className="px-6 py-4 border-b" style={{ borderColor: "var(--border-default)" }}>
          <h2 className="text-lg font-medium" style={{ color: "var(--text-primary)" }}>Buscas externas</h2>
          <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
            Atalhos para os canais ancorados (configure em <Link to="/configuracoes" className="gold-text underline">Configurações</Link>).
          </p>
        </div>
        <div className="p-6 flex flex-wrap gap-2">
          {Object.entries(links).map(([k, v]) => (
            <a
              key={k}
              href={v}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost px-3 py-2 text-xs rounded-sm flex items-center gap-1"
              data-testid={`ext-${k}`}
            >
              <Anchor size={11} weight="duotone" /> {k} <ArrowUpRight size={12} />
            </a>
          ))}
        </div>
      </section>

      <section className="surface" data-testid="offers-table">
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border-default)" }}>
          <h2 className="text-lg font-medium" style={{ color: "var(--text-primary)" }}>Ofertas encontradas</h2>
          <span className="label-eyebrow">
            {offers.length} resultados · ordenado por R$/{unitLabel}
          </span>
        </div>
        {offers.length === 0 ? (
          <div className="p-8 text-sm" style={{ color: "var(--text-secondary)" }}>
            Nenhuma oferta retornada. Tente atualizar.
          </div>
        ) : (
          <div className="table-scroll">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left" style={{ color: "var(--text-secondary)" }}>
                  <th className="px-6 py-3 label-eyebrow">Anúncio</th>
                  <th className="px-6 py-3 label-eyebrow">Peso</th>
                  <th className="px-6 py-3 label-eyebrow">Canal</th>
                  <th className="px-6 py-3 label-eyebrow">Vendedor</th>
                  <th className="px-6 py-3 label-eyebrow">Vendidos</th>
                  <th className="px-6 py-3 label-eyebrow text-right">Preço bruto</th>
                  <th className="px-6 py-3 label-eyebrow text-right">R$/{unitLabel}</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {offers.map((o, idx) => {
                  const isAnchor = anchoredSet.has((o.channel || "").toLowerCase());
                  return (
                    <tr key={`${o.url}-${idx}`} className="border-t surface-hover" style={{ borderColor: "var(--border-default)" }}>
                      <td className="px-6 py-3 max-w-md" style={{ color: "var(--text-primary)" }}>{o.title}</td>
                      <td className="px-6 py-3 text-xs" style={{ color: o.weight_label ? "var(--text-secondary)" : "var(--text-muted)" }}>
                        {o.weight_label || "n/d"}
                      </td>
                      <td className="px-6 py-3">
                        <span
                          className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 inline-flex items-center gap-1"
                          style={{
                            background: isAnchor ? "rgba(212,175,55,0.08)" : "var(--bg-surface-hover)",
                            color: isAnchor ? "var(--gold)" : "var(--text-secondary)",
                            border: `1px solid ${isAnchor ? "rgba(212,175,55,0.2)" : "var(--border-default)"}`,
                          }}
                        >
                          {isAnchor && <Anchor size={9} weight="fill" />}
                          {o.channel}
                        </span>
                      </td>
                      <td className="px-6 py-3" style={{ color: "var(--text-secondary)" }}>{o.seller}</td>
                      <td className="px-6 py-3" style={{ color: "var(--text-muted)" }}>{o.sold_quantity ?? "—"}</td>
                      <td className="px-6 py-3 text-right" style={{ color: "var(--text-primary)" }}>{formatBRL(o.price)}</td>
                      <td className="px-6 py-3 text-right font-medium" style={{ color: o.normalized_price ? "var(--gold)" : "var(--text-muted)" }}>
                        {o.normalized_price ? formatPerWeight(o.normalized_price, unit) : "—"}
                      </td>
                      <td className="px-6 py-3 text-right">
                        <a href={o.url} target="_blank" rel="noreferrer" className="btn-ghost px-2 py-1 inline-flex items-center gap-1 text-xs">
                          Abrir <ArrowUpRight size={12} />
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

const Stat = ({ label, value, icon: Icon, color }) => (
  <div className="surface p-4">
    <div className="flex items-start justify-between">
      <div>
        <div className="label-eyebrow mb-2">{label}</div>
        <div className="text-2xl font-light" style={{ color: color || "var(--text-primary)" }}>{value}</div>
      </div>
      <Icon size={18} weight="duotone" style={{ color: color || "var(--gold)" }} />
    </div>
  </div>
);
